import Hero from '../components/Hero.jsx';
import PulsarDropsEconomics from '../components/PulsarDropsEconomics.jsx';
import Section from '../components/Section.jsx';
import WirexRailEconomics from '../components/WirexRailEconomics.jsx';
import Plans from '../components/Plans.jsx';
import StrategicPartners from '../components/StrategicPartners.jsx';
import { fmtCompactEur, fmtCompactUsers, fmtEur2 } from '../formatters.js';
import { MODEL_ASSUMPTIONS, MODEL_CHECKPOINTS, REVENUE_LINES } from '../modelSnapshot.js';

const { m0, m6, m12 } = MODEL_CHECKPOINTS;

const REVENUE_STREAMS = REVENUE_LINES.map((line, index) => [
  String(index + 1).padStart(2, '0'),
  line.shortLabel,
  `${fmtCompactEur(line.m12MonthlyRevenue)} M12 monthly revenue; ${fmtEur2(
    line.monthlyPerCardholder,
  )} per cardholder / mo. ${line.note}`,
]);

const COMMERCIAL_MOMENTUM = [
  ['Opening ARR', fmtCompactEur(m0.totalArr), 'Existing B2B ARR at M0.'],
  ['M6 ARR', fmtCompactEur(m6.totalArr), 'B2B ARR plus 30K B2C cardholders.'],
  ['M12 ARR', fmtCompactEur(m12.totalArr), 'Base-case total ARR / run-rate target.'],
  ['M12 cardholders', fmtCompactUsers(m12.cardholders), 'Active B2C cardholder target.'],
];

const REVENUE_ESTIMATES = [
  [
    'B2C ARR',
    fmtCompactEur(m12.b2cArr),
    'Annualized Month 12 consumer revenue from six fee streams.',
  ],
  ['B2B ARR', fmtCompactEur(m12.b2bArr), 'B2B run-rate scales from the existing $750K ARR base.'],
  [
    'B2C monthly revenue',
    fmtCompactEur(MODEL_ASSUMPTIONS.month12B2cMonthlyRevenue),
    'Month 12 consumer revenue before annualizing to ARR.',
  ],
  [
    'Known monthly costs',
    fmtCompactEur(MODEL_ASSUMPTIONS.month12KnownMonthlyCosts),
    'Month 12 consumer opex, marketing, fintech stack, compliance, and legal.',
  ],
];

const UNIT_ECONOMICS = [
  [
    'B2C revenue / cardholder',
    `${fmtEur2(MODEL_ASSUMPTIONS.month12RevenuePerCardholder)} / mo`,
    'Blended Month 12 consumer revenue across trading, subscriptions, FX, credit, interchange, and yield.',
  ],
  [
    'Annualized / cardholder',
    `${fmtEur2(m12.b2cArr / m12.cardholders)} / yr`,
    'Month 12 B2C ARR divided by the 100K cardholder target.',
  ],
  [
    'B2B ARR expansion',
    `${fmtCompactEur(m0.b2bArr)} → ${fmtCompactEur(m12.b2bArr)}`,
    'Existing B2B revenue run-rate doubles over the 12-month plan.',
  ],
  [
    'Yield deposit base',
    fmtCompactEur(MODEL_ASSUMPTIONS.month12YieldDeposits),
    'Month 12 deposit base behind the retained yield line.',
  ],
];

const B2B_VERTICALS = [
  ['Existing B2B base', 'The plan starts with $750K B2B ARR already in the model.'],
  ['Revenue growth path', 'B2B ARR scales steadily to $1.5M by Month 12.'],
  [
    'Partner modules',
    'Campaigns, loyalty, and partner-facing modules stay separate from B2C economics.',
  ],
  ['Expansion discipline', 'Report signed B2B ARR separately from upside pipeline and pilots.'],
];

const FLYWHEEL = [
  ['Money Back campaigns', 'Launch visible reward moments that create sharing and repeat spend.'],
  ['Referral rewards', 'Pay for growth when the new user activates, not before.'],
  ['Subsidized on-ramping', 'Reduce the first funding friction for high-intent users.'],
  ['Free FX allowance', 'Give travelers a concrete reason to test the card.'],
  ['Pro upgrade prompts', 'Push upgrades at FX, card, and yield moments when value is obvious.'],
  ['Partner-funded campaigns', 'Let ecosystem partners co-fund targeted acquisition.'],
];

const RAISE_UNLOCKS = [
  ['B2C cardholders', fmtCompactUsers(m0.cardholders), fmtCompactUsers(m12.cardholders), 'M12'],
  ['B2C ARR', fmtCompactEur(m0.b2cArr), fmtCompactEur(m12.b2cArr), 'M12'],
  ['B2B ARR', fmtCompactEur(m0.b2bArr), fmtCompactEur(m12.b2bArr), 'M12'],
  ['Total ARR', fmtCompactEur(m0.totalArr), fmtCompactEur(m12.totalArr), 'M12'],
  ['Midpoint checkpoint', fmtCompactEur(m6.totalArr), '30K cardholders', 'M6'],
];

const RISKS = [
  {
    risk: 'Cardholder ramp',
    proof: 'B2C cardholders reach 30K by M6 and 100K by M12.',
    control:
      'Track funded wallets, card applications, card activation, and active cardholders weekly.',
  },
  {
    risk: 'B2C fee mix',
    proof: 'Six B2C revenue streams blend to $3.96 per active cardholder / mo by M12.',
    control:
      'Report trading, subscription, FX/remittance, credit, interchange, and yield separately.',
  },
  {
    risk: 'B2B growth',
    proof: 'Existing B2B ARR grows from $750K at M0 to $1.5M by M12.',
    control:
      'Keep signed B2B ARR separate from pilots, partner pipeline, and non-recurring setup fees.',
  },
  {
    risk: 'Yield product scale',
    proof: 'Yield revenue assumes a $75M Month 12 deposit base.',
    control:
      'Track eligible balances, retained yield share, reward fee, and partner product conversion.',
  },
  {
    risk: 'Cost discipline',
    proof: 'Known consumer costs are held near $206K / mo at M12.',
    control:
      'Track opex, CAC/marketing, fintech stack cost, compliance, and legal as separate monthly lines.',
  },
];

export default function SimplePage({ inputs, summary }) {
  return (
    <>
      <Hero />

      <Section
        kicker="Model baseline"
        title="The latest plan starts from existing B2B ARR and ramps B2C cardholders."
        lede="The linked Pulsar Financial model starts with $750K B2B ARR at M0, then layers a B2C cardholder ramp from zero to 100K by Month 12. The Month 12 target is $6.26M total ARR / run-rate."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {COMMERCIAL_MOMENTUM.map(([label, value, sub]) => (
            <Stat key={label} label={label} value={value} sub={sub} />
          ))}
        </div>
      </Section>

      <Section
        kicker="12-month target"
        title="B2C becomes the larger revenue engine by Month 12."
        lede="At Month 12, B2C contributes $4.76M ARR from 100K active cardholders while B2B contributes $1.5M ARR. The story is not a one-line card interchange model; it is a stacked consumer revenue engine supported by a recurring B2B base."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="B2C ARR" value={fmtCompactEur(m12.b2cArr)} sub="M12 annualized" />
          <Stat label="B2B ARR" value={fmtCompactEur(m12.b2bArr)} sub="M12 run-rate" />
          <Stat label="Revenue / cardholder" value="$3.96" sub="M12 monthly blend" />
          <Stat
            label="Known monthly costs"
            value={fmtCompactEur(MODEL_ASSUMPTIONS.month12KnownMonthlyCosts)}
            sub="M12 cost base"
          />
        </div>
      </Section>

      <Section
        kicker="Strategic partner stack"
        title="Pulsar is built around close strategic partners."
        lede="The launch plan combines card rails, stablecoin infrastructure, embedded wallets, ecosystem distribution, and payment partners into one operating stack."
      >
        <StrategicPartners />
      </Section>

      <Section
        kicker="Revenue engine"
        title="Revenue sources, estimates, and unit economics."
        lede="The new sheet underwrites B2C from the bottom up: revenue per active cardholder across trading, subscriptions, FX/remittance, credit, interchange, and yield. B2B ARR is shown separately so partner revenue does not blur consumer wallet economics."
        tone="ice"
      >
        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr] mb-5">
          <div className="pulsar-surface rounded-card p-6">
            <p className="caption text-carbon-400">Revenue estimate bridge</p>
            <div className="mt-5 divide-y divide-carbon-100">
              {REVENUE_ESTIMATES.map(([source, estimate, body]) => (
                <div key={source} className="grid gap-2 py-4 sm:grid-cols-[0.85fr_0.45fr_1.2fr]">
                  <p className="label text-carbon-800">{source}</p>
                  <p className="label text-carbon-900 num-tabular">{estimate}</p>
                  <p className="label text-carbon-500">{body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="pulsar-surface-dark rounded-card p-6 text-white">
            <p className="caption text-ice-300">Unit economics sanity check</p>
            <div className="mt-5 space-y-4">
              {UNIT_ECONOMICS.map(([label, value, sub]) => (
                <div key={label} className="border-t border-white/10 pt-4">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="label text-carbon-300">{label}</p>
                    <p className="text-2xl font-semibold num-tabular">{value}</p>
                  </div>
                  <p className="caption text-carbon-400 mt-2">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="caption text-carbon-400 mb-3">Six B2C monetization lines</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {REVENUE_STREAMS.map(([num, title, body]) => (
            <RevenueStream key={title} num={num} title={title} body={body} />
          ))}
        </div>

        <div className="pulsar-surface rounded-card p-6 mt-5">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <p className="caption text-carbon-400">Partner/payment expansion</p>
              <h3 className="text-xl font-semibold mt-2">Partner and payment expansion lines.</h3>
            </div>
            <p className="label text-carbon-500 max-w-md">
              These lines are reported separately from consumer wallet economics so the core B2C
              story stays clean.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-5">
            {B2B_VERTICALS.map(([title, body]) => (
              <div key={title} className="border-t border-carbon-100 pt-3">
                <p className="label text-carbon-800">{title}</p>
                <p className="label text-carbon-500 mt-1">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section
        kicker="Card plans"
        title="The consumer packaging that turns card usage into Pro conversion."
        lede="Free gives users a low-friction virtual card and a reason to try the wallet. Pro is the monetization layer: higher limits, travel utility, Earn, Money Back boosts, and a premium card experience."
        tone="soft"
      >
        <Plans tiers={inputs.tiers} stablecoin={inputs.stablecoin} />
      </Section>

      <Section
        kicker="Strategic rail layer"
        title="Faster path to cards, accounts, and payment revenue."
        lede="The investor point is simple: Wirex helps Pulsar launch the card and account layer faster instead of building the full issuing stack internally."
      >
        <WirexRailEconomics />
      </Section>

      <Section
        kicker="Growth and Money Back flywheel"
        title="Turn acquisition into funded, card-active users."
        lede="Cashback and Money Back campaigns are still a launch hook, but the economics page now measures them against the Month 12 cardholder and revenue targets. Incentives have to drive funded wallets, card activation, and repeat spend."
        tone="soft"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {FLYWHEEL.map(([title, body]) => (
            <FlywheelStep key={title} title={title} body={body} />
          ))}
        </div>
        <PulsarDropsEconomics summary={summary} variant="investor" />
      </Section>

      <Section
        kicker="The $4M raise"
        title="$4M raise."
        lede="The raise funds the Month 12 launch plan across acquisition, card activation, wallet liquidity, partner campaigns, compliance, and operating runway. The milestone set is now explicit: 100K B2C cardholders, $4.76M B2C ARR, $1.5M B2B ARR, and $6.26M total ARR / run-rate."
        tone="mint"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Raise" value="$4M" sub="Seed round" />
          <Stat label="Cardholders" value={fmtCompactUsers(m12.cardholders)} sub="M12 B2C target" />
          <Stat label="B2C ARR" value={fmtCompactEur(m12.b2cArr)} sub="M12 annualized" />
          <Stat label="Total ARR" value={fmtCompactEur(m12.totalArr)} sub="M12 run-rate" />
        </div>

        <div className="pulsar-surface rounded-card p-6 mb-4">
          <p className="caption text-carbon-400">Use of funds</p>
          <p className="body-md text-carbon-600 mt-3">
            Growth campaigns, card activation, wallet operations, liquidity availability, partner
            integrations, compliance, and operating runway. The point is one launch machine that can
            hit the M6 checkpoint and the M12 revenue target.
          </p>
        </div>
      </Section>

      <Section kicker="Raise impact" title="What the raise has to unlock.">
        <RaiseUnlocks rows={RAISE_UNLOCKS} />
      </Section>

      <Section
        kicker="Execution risks"
        title="What we need to prove."
        lede="The plan depends on measurable execution variables. The controls are built around cardholder-ramp tracking, revenue-line separation, B2B ARR discipline, yield balance validation, and cost reporting."
        tone="soft"
      >
        <RiskMatrix risks={RISKS} />
      </Section>

      <footer className="border-t border-carbon-100 py-12">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between text-xs text-carbon-400">
          <span>Pulsar Economics — Private model · v2.0</span>
          <span className="num-tabular">
            {m12.cardholders.toLocaleString('en-US')} active cardholders in M12 model
          </span>
        </div>
      </footer>
    </>
  );
}

function RevenueStream({ num, title, body }) {
  return (
    <div className="pulsar-widget rounded-box p-5">
      <p className="caption text-ice-700 num-tabular">{num}</p>
      <h3 className="text-base font-semibold leading-snug mt-3">{title}</h3>
      <p className="body-md text-carbon-500 mt-3">{body}</p>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="pulsar-widget rounded-box p-5">
      <p className="caption text-carbon-400">{label}</p>
      <p className="text-2xl md:text-3xl font-semibold num-tabular mt-2 leading-none">{value}</p>
      {sub && <p className="text-xs text-carbon-400 mt-2">{sub}</p>}
    </div>
  );
}

function FlywheelStep({ title, body }) {
  return (
    <div className="pulsar-widget rounded-box p-5">
      <h3 className="text-base font-semibold leading-snug">{title}</h3>
      <p className="body-md text-carbon-500 mt-3">{body}</p>
    </div>
  );
}

function RaiseUnlocks({ rows }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="pulsar-surface rounded-card p-6">
        <p className="caption text-carbon-400">Opening checkpoint</p>
        <h3 className="text-xl font-semibold mt-2">M0 baseline.</h3>
        <div className="mt-5 divide-y divide-carbon-100">
          {rows.map(([label, without]) => (
            <div key={label} className="flex items-baseline justify-between gap-4 py-3">
              <p className="label text-carbon-600">{label}</p>
              <p className="label text-carbon-900 num-tabular text-right">{without}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="pulsar-surface-dark rounded-card p-6 text-white">
        <p className="caption text-ice-300">Target checkpoint</p>
        <h3 className="text-xl font-semibold mt-2">M12 model target.</h3>
        <div className="mt-5 divide-y divide-white/10">
          {rows.map(([label, , withRaise, multiple]) => (
            <div key={label} className="grid grid-cols-[1fr_auto_auto] items-baseline gap-4 py-3">
              <p className="label text-carbon-300">{label}</p>
              <p className="label text-white num-tabular text-right">{withRaise}</p>
              <span className="caption rounded-full bg-mint-300 px-2.5 py-1 text-carbon-900">
                {multiple}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RiskMatrix({ risks }) {
  return (
    <div className="pulsar-surface rounded-card overflow-hidden">
      <div className="hidden md:grid grid-cols-[0.9fr_1.25fr_1.45fr] gap-6 bg-carbon-50 px-6 py-3 caption text-carbon-400">
        <span>Risk</span>
        <span>What must be true</span>
        <span>Control / metric</span>
      </div>
      <div className="divide-y divide-carbon-100">
        {risks.map((item) => (
          <div
            key={item.risk}
            className="grid gap-3 md:grid-cols-[0.9fr_1.25fr_1.45fr] md:gap-6 px-6 py-5"
          >
            <div>
              <p className="caption text-carbon-400 md:hidden">Risk</p>
              <h3 className="text-base font-semibold leading-snug text-carbon-800">{item.risk}</h3>
            </div>
            <div>
              <p className="caption text-carbon-400 md:hidden">What must be true</p>
              <p className="body-md text-carbon-600">{item.proof}</p>
            </div>
            <div>
              <p className="caption text-carbon-400 md:hidden">Control / metric</p>
              <p className="body-md text-carbon-600">{item.control}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
